/**
 * Request-time pruning of stale tool results.
 *
 * Large tool outputs (a 50 KB `read`, a long `bash` log) are only useful for a
 * few turns. Long before compaction triggers they are dead weight in every
 * provider request. This module replaces the content of old, large tool
 * results with a short placeholder right before the request is built.
 *
 * It is a pure transform: the session file, compaction, and in-memory history
 * are never touched. The model can re-read or re-run whenever it needs the
 * original output.
 */

import type { AgentMessage } from "@elyracode/agent-core";
import type { ToolCall, ToolResultMessage } from "@elyracode/ai";

export interface ContextPruningSettings {
	/** Master switch. When false, messages pass through untouched. */
	enabled: boolean;
	/** A tool result is stale once this many assistant messages follow it. */
	keepRecentTurns: number;
	/** Only prune tool results whose text content is at least this long. */
	minChars: number;
}

export const DEFAULT_CONTEXT_PRUNING: ContextPruningSettings = {
	enabled: true,
	keepRecentTurns: 6,
	minChars: 2000,
};

export interface ContextPruningStats {
	prunedMessages: number;
	charsRemoved: number;
}

const COMMAND_PREVIEW_MAX_CHARS = 80;

function isToolResultMessage(message: AgentMessage): message is ToolResultMessage {
	return message.role === "toolResult";
}

function textLength(result: ToolResultMessage): number {
	let total = 0;
	for (const block of result.content) {
		if (block.type === "text") total += block.text.length;
	}
	return total;
}

function hasImageContent(result: ToolResultMessage): boolean {
	return result.content.some((block) => block.type === "image");
}

function joinText(result: ToolResultMessage): string {
	const parts: string[] = [];
	for (const block of result.content) {
		if (block.type === "text") parts.push(block.text);
	}
	return parts.join("\n");
}

function countLines(text: string): number {
	if (text.length === 0) return 0;
	const lines = text.split("\n");
	if (lines[lines.length - 1] === "") lines.pop();
	return lines.length;
}

function stringArg(args: Record<string, unknown>, key: string): string | undefined {
	const value = args[key];
	return typeof value === "string" && value.length > 0 ? value : undefined;
}

function numberArg(args: Record<string, unknown>, key: string): number | undefined {
	const value = args[key];
	return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function commandPreview(command: string): string {
	const firstLine = command.split("\n")[0]?.trim() ?? "";
	if (firstLine.length <= COMMAND_PREVIEW_MAX_CHARS) return firstLine;
	return `${firstLine.slice(0, COMMAND_PREVIEW_MAX_CHARS - 3)}...`;
}

function buildPlaceholder(toolCall: ToolCall, text: string): string {
	const lines = countLines(text);
	const args: Record<string, unknown> = toolCall.arguments;

	switch (toolCall.name) {
		case "read": {
			const path = stringArg(args, "path");
			if (path) {
				const offset = numberArg(args, "offset");
				const offsetNote = offset !== undefined ? ` (offset ${offset})` : "";
				return `[read ${path}${offsetNote}, ${lines} lines – pruned from context; re-read if needed]`;
			}
			return `[read result pruned – ${lines} lines]`;
		}
		case "bash": {
			const kb = Math.max(1, Math.round(text.length / 1024));
			const command = stringArg(args, "command");
			if (command) {
				return `[bash \`${commandPreview(command)}\` – output pruned (${lines} lines, ${kb} KB); re-run if needed]`;
			}
			return `[bash output pruned – ${lines} lines, ${kb} KB; re-run if needed]`;
		}
		case "grep":
		case "find":
		case "ls":
			return `[${toolCall.name} results pruned – ${lines} lines; re-run if needed]`;
		default:
			return `[${toolCall.name} result pruned – ${lines} lines]`;
	}
}

function collectToolCalls(messages: readonly AgentMessage[]): Map<string, ToolCall> {
	const calls = new Map<string, ToolCall>();
	for (const message of messages) {
		if (message.role !== "assistant") continue;
		for (const block of message.content) {
			if (block.type === "toolCall") calls.set(block.id, block);
		}
	}
	return calls;
}

/** For each index, the number of assistant messages at strictly later indices. */
function assistantTurnsAfter(messages: readonly AgentMessage[]): number[] {
	const counts = new Array<number>(messages.length);
	let seen = 0;
	for (let i = messages.length - 1; i >= 0; i--) {
		counts[i] = seen;
		if (messages[i].role === "assistant") seen++;
	}
	return counts;
}

interface PruneOutcome {
	messages: AgentMessage[];
	stats: ContextPruningStats;
}

function prune(messages: readonly AgentMessage[], settings: ContextPruningSettings): PruneOutcome {
	const stats: ContextPruningStats = { prunedMessages: 0, charsRemoved: 0 };
	if (!settings.enabled || messages.length === 0) {
		return { messages: messages as AgentMessage[], stats };
	}

	const keepRecentTurns = Math.max(0, Math.floor(settings.keepRecentTurns));
	const minChars = Math.max(0, settings.minChars);
	const toolCalls = collectToolCalls(messages);
	const turnsAfter = assistantTurnsAfter(messages);

	let result: AgentMessage[] | undefined;

	for (let i = 0; i < messages.length; i++) {
		const message = messages[i];
		if (!isToolResultMessage(message)) continue;
		if (turnsAfter[i] < keepRecentTurns) continue;
		if (message.isError) continue;
		if (hasImageContent(message)) continue;

		const originalChars = textLength(message);
		if (originalChars < minChars) continue;

		const toolCall = toolCalls.get(message.toolCallId);
		if (!toolCall) continue;

		const placeholder = buildPlaceholder(toolCall, joinText(message));
		const pruned: ToolResultMessage = {
			role: "toolResult",
			toolCallId: message.toolCallId,
			toolName: message.toolName,
			content: [{ type: "text", text: placeholder }],
			details: undefined,
			isError: message.isError,
			timestamp: message.timestamp,
		};

		if (!result) result = [...messages];
		result[i] = pruned;
		stats.prunedMessages++;
		stats.charsRemoved += originalChars - placeholder.length;
	}

	return { messages: result ?? (messages as AgentMessage[]), stats };
}

/**
 * Replace the content of stale, large, non-error tool results with a short
 * placeholder. Input messages are never mutated. Untouched messages are
 * returned by reference so the cached prompt prefix stays byte-identical.
 * When nothing is pruned the input array itself is returned.
 */
export function pruneStaleToolResults(
	messages: readonly AgentMessage[],
	settings: ContextPruningSettings,
): AgentMessage[] {
	return prune(messages, settings).messages;
}

/** Dry run of {@link pruneStaleToolResults} that only reports what would be removed. */
export function countPrunableChars(
	messages: readonly AgentMessage[],
	settings: ContextPruningSettings,
): ContextPruningStats {
	return prune(messages, settings).stats;
}
